<template>
  <div v-if="importing" class="riw-importing" aria-live="polite">
    <v-progress-circular indeterminate color="primary" size="56" width="4" />
    <h3 class="riw-importing__title">Importing {{ formatNumber(sentRows) }} rows…</h3>
    <p class="riw-importing__text">Keep this window open. It will unlock if the server takes longer than 30 seconds.</p>
    <div class="riw-phases">
      <div v-for="phase in phaseList" :key="phase.key" class="riw-phase" :class="`is-${phase.state}`">
        <v-progress-circular v-if="phase.state === 'run'" indeterminate color="primary" size="16" width="2" />
        <v-icon v-else size="18">{{ phase.state === 'done' ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
        <span>{{ phase.label }}</span>
      </div>
    </div>
  </div>

  <div v-else class="riw-panel-body">
    <div class="riw-complete">
      <div class="riw-complete__badge" :class="{ 'is-warn': tone === 'warn', 'is-err': tone === 'err' }">
        <v-icon>{{ tone === 'ok' ? 'mdi-check' : tone === 'warn' ? 'mdi-alert' : 'mdi-alert-circle-outline' }}</v-icon>
      </div>
      <h2 class="riw-complete__title">{{ heading }}</h2>
      <p class="riw-complete__sub">{{ sentence }}</p>

      <div v-if="succeeded || hasStats" class="riw-stats">
        <div class="riw-stat riw-stat--t"><v-icon>mdi-file-document-multiple-outline</v-icon><span class="riw-stat__v">{{ formatNumber(stats.imported) }}</span><span class="riw-stat__l">Records imported</span></div>
        <div class="riw-stat riw-stat--c"><v-icon>mdi-plus-circle-outline</v-icon><span class="riw-stat__v">{{ formatNumber(stats.created) }}</span><span class="riw-stat__l">New</span></div>
        <div class="riw-stat riw-stat--u"><v-icon>mdi-refresh-circle</v-icon><span class="riw-stat__v">{{ formatNumber(stats.updated) }}</span><span class="riw-stat__l">Updated</span></div>
        <div v-if="stats.notImported" class="riw-stat riw-stat--f" title="Rows the server did not save (validation errors, skipped rows)">
          <v-icon>mdi-alert-circle-outline</v-icon><span class="riw-stat__v">{{ formatNumber(stats.notImported) }}</span><span class="riw-stat__l">Failed / skipped</span>
        </div>
        <div v-if="stats.facilitySkipped" class="riw-stat riw-stat--f" title="Rows with a blank or unknown Facility value (part of Failed / skipped)">
          <v-icon>mdi-domain-off</v-icon><span class="riw-stat__v">{{ formatNumber(stats.facilitySkipped) }}</span><span class="riw-stat__l">Skipped — Facility</span>
        </div>
        <div v-if="stats.groupLevel" class="riw-stat riw-stat--g" title="Rows with no member Facility — visible to every Facility in the Facility Group">
          <v-icon>mdi-office-building-marker-outline</v-icon><span class="riw-stat__v">{{ formatNumber(stats.groupLevel) }}</span><span class="riw-stat__l">Saved at Facility Group level</span>
        </div>
      </div>

      <div v-if="notImported.length" class="riw-errors">
        <div class="riw-errors__head">
          <v-icon size="20">mdi-alert-circle</v-icon>
          <span>Rows not imported ({{ formatNumber(notImported.length) }})</span>
          <span class="riw-errors__spacer"></span>
          <v-btn small text @click="toggleAll">{{ anyOpen ? 'Collapse all' : 'Expand all' }}</v-btn>
        </div>
        <div class="riw-errors__list">
          <div v-for="(err, idx) in visibleErrors" :key="idx" class="riw-err" :class="{ 'is-open': open[idx] }">
            <button type="button" class="riw-err__sum" :aria-expanded="String(!!open[idx])" @click="toggle(idx)">
              <span class="riw-mini-chip riw-num">Row {{ rowNumberOf(err) }}</span>
              <span class="riw-mini-chip" :class="{ 'is-warn': err.wasSkipped }">{{ err.wasSkipped ? 'Skipped' : 'Failed' }}</span>
              <span class="riw-err__msg">{{ messageOf(err) }}</span>
              <v-icon size="18" class="riw-err__chev">mdi-chevron-down</v-icon>
            </button>
            <div v-if="open[idx]" class="riw-err__detail">
              <div class="riw-overline riw-err__label">Row data</div>
              <table class="riw-kv">
                <tbody>
                  <tr v-for="(val, key) in err.rowData" :key="key" :class="{ 'is-bad': key === badFieldOf(err) }">
                    <td>{{ key }}</td>
                    <td><template v-if="!isBlank(val)">{{ val }}</template><span v-else class="riw-dash">—</span></td>
                  </tr>
                </tbody>
              </table>
              <ul v-if="err.validationErrors && err.validationErrors.length" class="riw-err__msgs">
                <li v-for="(m, i) in err.validationErrors" :key="i">{{ m }}</li>
              </ul>
            </div>
          </div>
        </div>
        <p v-if="notImported.length > visibleErrors.length" class="riw-errors__cap">
          Showing the first {{ formatNumber(visibleErrors.length) }} of {{ formatNumber(notImported.length) }} rows.
        </p>
      </div>

      <!-- Imported rows whose Tag value matched no linkable Tag: a notice, not an error. -->
      <div v-if="notLinked.length" class="riw-errors riw-errors--info">
        <div class="riw-errors__head">
          <v-icon size="20">mdi-link-variant-off</v-icon>
          <span>Tags not linked ({{ formatNumber(notLinked.length) }})</span>
          <span class="riw-errors__spacer"></span>
          <v-btn small text @click="notLinkedOpen = !notLinkedOpen">{{ notLinkedOpen ? 'Hide' : 'Show' }}</v-btn>
        </div>
        <div v-if="notLinkedOpen" class="riw-errors__list">
          <p class="riw-errors__note">These rows were imported. Their Tag value did not match a Tag they can link to — connect them later from the register's Relationships.</p>
          <div v-for="(err, idx) in visibleNotLinked" :key="`nl-${idx}`" class="riw-err">
            <div class="riw-err__sum riw-err__sum--static">
              <span class="riw-mini-chip riw-num">Row {{ rowNumberOf(err) }}</span>
              <span class="riw-mini-chip">Not linked</span>
              <span class="riw-err__msg">{{ messageOf(err) }}</span>
            </div>
          </div>
        </div>
        <p v-if="notLinkedOpen && notLinked.length > visibleNotLinked.length" class="riw-errors__cap">
          Showing the first {{ formatNumber(visibleNotLinked.length) }} of {{ formatNumber(notLinked.length) }} rows.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  badFieldForError,
  classifyImportErrors,
  importErrorRowNumber,
  importResultSummary,
  isBlankCell,
} from '../../../modules/registerImportWizardModel';

const ERROR_RENDER_LIMIT = 200;

/**
 * Complete step. While importing: phases + the close lock (owned by ExcelUpload, with its 30 s
 * escape hatch). Afterwards: the server's statistics as stat cards and its per-row errors.
 * Every row is sent to the server (no client-side skipping since AVA-602), so all skipped/failed
 * rows listed here come from the server.
 */
export default {
  name: 'ImportCompleteStep',
  props: {
    importing: { type: Boolean, default: false },
    /** [{ key, label }] in order; `phase` is the key currently running. */
    phases: { type: Array, default: () => [] },
    phase: { type: String, default: '' },
    sentRows: { type: Number, default: 0 },
    importResults: { type: Object, default: null },
    registerName: { type: String, default: '' },
    groupName: { type: String, default: '' },
    facilityColumn: { type: String, default: '' },
    /** Mode an empty Facility Group register's first import set ('Shared' | 'Separated'), else ''. */
    modeSetByImport: { type: String, default: '' },
    /** Resulting mode ('Shared' | 'Separated' | '') — for the Shared sentence. */
    mode: { type: String, default: '' },
  },
  data: () => ({ open: { 0: true }, notLinkedOpen: false }),
  computed: {
    phaseList() {
      const current = this.phases.findIndex((p) => p.key === this.phase);
      return this.phases.map((p, index) => ({
        ...p,
        state: index < current ? 'done' : index === current ? 'run' : 'wait',
      }));
    },
    stats() {
      return importResultSummary(this.importResults);
    },
    hasStats() {
      return !!(this.importResults && this.importResults.statistics);
    },
    succeeded() {
      return !!(this.importResults && this.importResults.success);
    },
    errors() {
      const list = this.importResults && this.importResults.errors;
      return Array.isArray(list) ? list : [];
    },
    classified() {
      return classifyImportErrors(this.errors);
    },
    notImported() {
      return this.classified.notImported;
    },
    notLinked() {
      return this.classified.notLinked;
    },
    visibleErrors() {
      return this.notImported.slice(0, ERROR_RENDER_LIMIT);
    },
    visibleNotLinked() {
      return this.notLinked.slice(0, ERROR_RENDER_LIMIT);
    },
    anyOpen() {
      return Object.values(this.open).some(Boolean);
    },
    tone() {
      if (!this.succeeded) return 'err';
      // Unlinked Tags on imported rows are a notice; only rows that did not import are issues.
      return this.stats.notImported || this.notImported.length ? 'warn' : 'ok';
    },
    heading() {
      if (!this.succeeded) return 'No import completed';
      return this.tone === 'warn' ? 'Import finished with issues' : 'Import completed successfully';
    },
    sentence() {
      const parts = [];
      const message = this.importResults && this.importResults.message;
      parts.push(message || (this.succeeded
        ? `Your data has been processed for ${this.registerName}.`
        : 'The import did not complete.'));
      // The server's own message already reports the Facility Group level count when it sends one.
      if (!message && this.succeeded && this.mode === 'Shared' && this.stats.groupLevel) {
        parts.push(`Rows are saved at Facility Group level, so every Facility in ${this.groupName || 'the Facility Group'} sees them.`);
      }
      if (this.succeeded && this.modeSetByImport && this.stats.imported > 0) {
        parts.push(`This import set the register to ${this.modeSetByImport}; the mode can’t be changed later.`);
      }
      return parts.join(' ');
    },
  },
  watch: {
    importResults() {
      this.open = { 0: true };
      this.notLinkedOpen = false;
    },
  },
  methods: {
    isBlank: isBlankCell,
    formatNumber(value) {
      return Number(value || 0).toLocaleString('en-US');
    },
    rowNumberOf: importErrorRowNumber,
    messageOf(err) {
      return (err.validationErrors && err.validationErrors.join('; ')) || 'Validation error';
    },
    badFieldOf(err) {
      return badFieldForError(err, this.facilityColumn);
    },
    toggle(idx) {
      this.open = { ...this.open, [idx]: !this.open[idx] };
    },
    toggleAll() {
      this.open = this.anyOpen ? {} : Object.fromEntries(this.visibleErrors.map((_, i) => [i, true]));
    },
  },
};
</script>
